import { Suspense, useContext, useEffect, useState } from "react";

import Loading from "../../../common/components/ui/loading/Loading";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { getCompanyId } from "../../../common/services/company.api";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import { getWorks } from "../../../common/services/work.api";
import ModalWork from "./ModalWork";
import { IWork } from "../../../types/work.type";
import { maskCurrency } from "../../../common/helpers/mask.helpers";
import { AuthContext } from "../../../common/components/auth/authContext/AuthContext";

const STATUS_LABELS = {};

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  name: "Descrição",
  category: "Categoria",
  corrective: "Tipo",
  sellPrice: "Preço",
};

function TableWorks() {
  useContext(AuthContext);

  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [works, setWorks] = useState<IWork[]>([]);
  const [page, setPage] = useState<number>(1);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "",
  });

  useEffect(() => {
    async function loadWorks(filters) {
      setIsLoading(true);

      const result = await getWorks({
        ...filters,
        page,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setWorks(result.data);
      setIsLoading(false);
    }

    loadWorks(filters);
  }, [filters, page, companyId, setData, data.itemsPerPage, notifyError]);

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as IWork)._id }),
      permission: "edit:work",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  const formatTypeProduct = (types: {
    Corretivo: boolean;
    Preventivo: boolean;
    "Mau Uso": boolean;
  }) => {
    const values = Object.values(types);
    let text = "";
    Object.keys(types).map((type, i) => {
      if (values.filter((value) => value).length > 1 && values[i]) {
        text = `${text} ${type}  |`;
      } else if (values[i]) {
        text = type;
      }
    });
    text = text[text.length - 1] == "|" ? text.slice(0, text.length - 1) : text;
    return text == "" ? "Geral" : text;
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Serviços Cadastrados"
          placeholder="Digite a descrição ou a categoria."
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo Serviço"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="works"
          tableData={works as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={works as unknown as Record<string, unknown>[]}
          actions={actions}
          renders={{
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            corrective: (_: any, _data) => {
              const data = _data as IWork;
              return (
                <p>
                  {formatTypeProduct({
                    "Mau Uso": data.misuse,
                    Corretivo: data.corrective,
                    Preventivo: data.preventive,
                  })}
                </p>
              );
            },
            sellPrice: (currency) => maskCurrency(currency),
          }}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          entity="works"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("create") && (
          <ModalWork
            id={action.data as string}
            onClose={cleanActionData}
            onSave={cleanActionData}
          ></ModalWork>
        )}
        {isAction("edit") && (
          <ModalWork
            id={action.data as string}
            onClose={cleanActionData}
            onSave={cleanActionData}
          ></ModalWork>
        )}
      </div>
    </Suspense>
  );
}

export default TableWorks;
